import Icon from '@components/v2/ui/Icon/Icon';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  SxProps,
  Theme,
} from '@mui/material';
import { ChevronDown, X } from 'lucide-react';
import { Ref, forwardRef } from 'react';
import { ChipConfig } from '../_components/ChipList/chipList.types';
import { AutocompleteTextFieldProps } from './autocomplete.types';
import useAutocompleteRenderProps from './hooks/useAutocompleteRenderProps';
import useLockedSelection from './hooks/useLockedSelection';

const CHIP_LIST_SINGLE_ROW_SX: SxProps<Theme> = {
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
};

type BaseProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = Pick<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  | 'options'
  | 'value'
  | 'onChange'
  | 'inputValue'
  | 'onInputChange'
  | 'multiple'
  | 'getOptionLabel'
  | 'isOptionEqualToValue'
  | 'loading'
  | 'disabled'
  | 'readOnly'
  | 'fullWidth'
  | 'size'
  | 'className'
  | 'sx'
  | 'slotProps'
  | 'renderOption'
  | 'filterOptions'
  | 'noOptionsText'
  | 'loadingText'
  | 'disableClearable'
  | 'disableCloseOnSelect'
  | 'filterSelectedOptions'
  | 'freeSolo'
  | 'autoHighlight'
  | 'selectOnFocus'
  | 'clearOnBlur'
  | 'handleHomeEndKeys'
  | 'open'
  | 'onOpen'
  | 'onClose'
  | 'onBlur'
  | 'onFocus'
  | 'id'
  | 'autoFocus'
> & {
  textFieldProps?: AutocompleteTextFieldProps;
  getChipConfig?: (value: T) => ChipConfig | undefined;
  testId?: string;
};

export type ShowAllChipsProp<Multiple extends boolean | undefined> = {
  showAllChips?: Multiple extends true ? boolean : never;
};

type Props<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = BaseProps<T, Multiple, DisableClearable, FreeSolo> & ShowAllChipsProp<Multiple>;

function Autocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    size = 'medium',
    fullWidth = true,
    options,
    value,
    onChange,
    inputValue,
    onInputChange,
    multiple,
    getOptionLabel,
    isOptionEqualToValue,
    loading,
    disabled,
    readOnly,
    className,
    sx,
    slotProps,
    renderOption,
    filterOptions,
    noOptionsText,
    loadingText,
    disableClearable,
    disableCloseOnSelect,
    filterSelectedOptions,
    freeSolo,
    autoHighlight,
    selectOnFocus,
    clearOnBlur,
    handleHomeEndKeys,
    open,
    onOpen,
    onClose,
    onBlur,
    onFocus,
    id,
    autoFocus,
    textFieldProps = {},
    getChipConfig,
    showAllChips,
    testId,
  }: BaseProps<T, Multiple, DisableClearable, FreeSolo> & { showAllChips?: boolean },
  ref: Ref<HTMLInputElement>,
) {
  const {
    renderInput,
    renderOption: resolvedRenderOption,
    renderValue,
    noOptionsText: resolvedNoOptionsText,
    loadingText: resolvedLoadingText,
  } = useAutocompleteRenderProps({
    multiple,
    loading,
    size,
    fullWidth,
    getOptionLabel,
    renderOption,
    getChipConfig,
    showAllChips,
    noOptionsText,
    loadingText,
    textFieldProps: { ...textFieldProps, autoFocus },
    inputRef: ref,
    testId,
  });

  const { getOptionDisabled, handleChange } = useLockedSelection({
    value,
    multiple,
    isOptionEqualToValue,
    getChipConfig,
    onChange,
  });

  return (
    <MuiAutocomplete
      options={options}
      value={value}
      inputValue={inputValue}
      multiple={multiple}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      disabled={disabled}
      readOnly={readOnly}
      fullWidth={fullWidth}
      size={size}
      className={className}
      sx={[CHIP_LIST_SINGLE_ROW_SX, ...(Array.isArray(sx) ? sx : [sx])]}
      slotProps={slotProps}
      filterOptions={filterOptions}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect ?? multiple}
      filterSelectedOptions={filterSelectedOptions}
      freeSolo={freeSolo}
      autoHighlight={autoHighlight}
      selectOnFocus={selectOnFocus}
      clearOnBlur={clearOnBlur}
      handleHomeEndKeys={handleHomeEndKeys}
      open={open}
      id={id}
      getOptionDisabled={getOptionDisabled}
      onChange={handleChange}
      onInputChange={onInputChange}
      onOpen={onOpen}
      onClose={onClose}
      onBlur={onBlur}
      onFocus={onFocus}
      noOptionsText={resolvedNoOptionsText}
      loadingText={resolvedLoadingText}
      popupIcon={<Icon icon={ChevronDown} size="md" />}
      clearIcon={<Icon icon={X} size="md" />}
      renderInput={renderInput}
      renderOption={resolvedRenderOption}
      renderValue={renderValue}
    />
  );
}

const ForwardedAutocomplete = forwardRef(Autocomplete);
ForwardedAutocomplete.displayName = 'Autocomplete';

export type { Props as AutocompleteProps };
export default ForwardedAutocomplete as <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: Props<T, Multiple, DisableClearable, FreeSolo> & { ref?: Ref<HTMLInputElement> },
) => React.ReactElement;
